import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useUrlState from '../../hooks/useUrlState';
import { useAuth } from '../../auth/AuthProvider';
import Header from '../../components/Header';
import StatusBadge from '../../components/StatusBadge';
import { styles, colors, radius } from '../../styles/theme';
import { PERMISSIONS, hasPermission } from '../../auth/permissions';
import useCachedQuery, { invalidateQueries } from '../../hooks/useCachedQuery';
import useDebouncedValue from '../../hooks/useDebouncedValue';
import { searchMatchPre } from '../../utils/searchMatch';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const getAuthHeaders = (extra = {}) => {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}`, ...extra } : extra;
};

const TalepListesiPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [filterStatus, setFilterStatus] = useState('BEKLENIYOR');
  // Arama URL'e bağlı: başka sayfaya gidip geri dönünce (tarayıcı-geri) korunur.
  const [search, setSearch] = useUrlState('arama', '', { debounceMs: 300 });
  const [message, setMessage] = useState('');

  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [targetTalep, setTargetTalep] = useState(null);
  const userCacheKey = user?.id || user?.email || user?.name || 'anon';

  const debouncedSearch = useDebouncedValue(search, 300);

  const {
    data: talepler = [],
    isLoading: taleplerLoading,
    isValidating: taleplerValidating,
    mutate: mutateTalepler,
    revalidate: revalidateTalepler
  } = useCachedQuery(
    ['talep-listesi', userCacheKey, 'talepler'],
    async () => {
      const res = await fetch(`${API_URL}/api/talepler`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      return Array.isArray(data) ? data : [];
    },
    { staleTime: 15000 }
  );


  const {
    data: lokasyonlar = [],
    isLoading: lokasyonlarLoading,
    isValidating: lokasyonlarValidating
  } = useCachedQuery(
    ['talep-listesi', userCacheKey, 'lokasyonlar'],
    async () => {
      const res = await fetch(`${API_URL}/api/lokasyonlar`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      return Array.isArray(data) ? data : [];
    },
    { staleTime: 60000 }
  );

  const {
    data: malzemeler = [],
    isLoading: malzemelerLoading,
    isValidating: malzemelerValidating
  } = useCachedQuery(
    ['talep-listesi', userCacheKey, 'malzemeler'],
    async () => {
      const res = await fetch(`${API_URL}/api/malzemeler`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      return Array.isArray(data) ? data : [];
    },
    { staleTime: 60000 }
  );

  const loading = taleplerLoading || lokasyonlarLoading || malzemelerLoading;
  const isValidating = taleplerValidating || lokasyonlarValidating || malzemelerValidating;

  const canViewAllRequests = hasPermission(user?.role, PERMISSIONS.VIEW_ALL_REQUESTS);
  const canApproveRequest = hasPermission(user?.role, PERMISSIONS.APPROVE_REQUEST);
  const canRejectRequest = hasPermission(user?.role, PERMISSIONS.REJECT_REQUEST);
  const canConvertToOrder = hasPermission(user?.role, PERMISSIONS.CONVERT_TO_ORDER);

  const filteredTalepler = useMemo(() => {
    let list = talepler;

    if (!canViewAllRequests) {
      list = list.filter(t => t.talep_eden_ad === user?.name);
    }

    if (filterStatus !== 'ALL') {
      list = list.filter(t => t.durum === filterStatus);
    }

    const q = (debouncedSearch || '').trim().toLowerCase();
    if (!q) return list;

    return list.filter(t => {
      const lokasyonAd = t.lokasyon_ad || lokasyonlar.find(l => String(l.id) === String(t.sube))?.ad || t.sube;
      const malzemeAd = t.malzeme_ad || malzemeler.find(m => String(m.id) === String(t.malzeme))?.ad || t.malzeme;

      return (
        searchMatchPre(t.talep_no, q) ||
        searchMatchPre(lokasyonAd, q) ||
        searchMatchPre(malzemeAd, q) ||
        searchMatchPre(t.talep_eden_ad || t.talep_eden, q)
      );
    });
  }, [talepler, canViewAllRequests, user, filterStatus, debouncedSearch, lokasyonlar, malzemeler]);

  const handleApproveTalep = async (talepId) => {
    const previousData = mutateTalepler(prev => {
      const list = Array.isArray(prev) ? prev : [];
      return list.map(item => (item.id === talepId ? { ...item, durum: 'ONAYLANDI' } : item));
    }, { revalidate: false });

    setMessage('Talep onaylandı!');
    setTimeout(() => setMessage(''), 3000);

    try {
      const res = await fetch(`${API_URL}/api/talepler/${talepId}/onayla`, {
        method: 'PATCH',
        headers: getAuthHeaders()
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      invalidateQueries(['talep', 'dashboard', 'siparis']);
      await revalidateTalepler({ force: true });
    } catch (err) {
      mutateTalepler(previousData, { revalidate: false });
      setMessage('Talep onaylanamadi.');
      setTimeout(() => setMessage(''), 3000);
    }
  };

  const handleRejectClick = (talep) => {
    setTargetTalep(talep);
    setShowRejectModal(true);
  };

  const confirmReject = async () => {
    if (!rejectReason) {
      alert('Lütfen red sebebi giriniz.');
      return;
    }

    if (!targetTalep?.id) return;

    const targetId = targetTalep.id;
    const previousData = mutateTalepler(prev => {
      const list = Array.isArray(prev) ? prev : [];
      return list.map(item => (
        item.id === targetId
          ? { ...item, durum: 'REDDEDILDI', red_nedeni: rejectReason }
          : item
      ));
    }, { revalidate: false });

    setShowRejectModal(false);
    setRejectReason('');
    setTargetTalep(null);
    setMessage('Talep reddedildi.');
    setTimeout(() => setMessage(''), 3000);

    try {
      const res = await fetch(`${API_URL}/api/talepler/${targetId}/reddet`, {
        method: 'PATCH',
        headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ reason: rejectReason })
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      invalidateQueries(['talep', 'dashboard', 'siparis']);
      await revalidateTalepler({ force: true });
    } catch (err) {
      mutateTalepler(previousData, { revalidate: false });
      setMessage('Talep reddedilemedi.');
      setTimeout(() => setMessage(''), 3000);
    }
  };

  return (
    <div>
      <Header title={canViewAllRequests ? 'Talep Yonetim Paneli' : 'Taleplerim & Durum Takibi'} />
      <div style={{ padding: '20px', background: colors.background, minHeight: 'calc(100vh - 60px)' }}>
        {message && (
          <div style={{ background: '#d4edda', padding: '12px 20px', marginBottom: '20px', borderRadius: radius.sm, color: '#155724', borderLeft: `4px solid ${styles.success}` }}>
            {message}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', gap: '10px', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <button
              onClick={() => setFilterStatus('ALL')}
              style={{
                padding: '10px 20px',
                background: filterStatus === 'ALL' ? styles.primary : 'white',
                color: filterStatus === 'ALL' ? 'white' : styles.secondary,
                border: filterStatus === 'ALL' ? 'none' : `1px solid ${colors.border}`,
                borderRadius: radius.sm,
                cursor: 'pointer'
              }}
            >
              Tumu
            </button>
            <button
              onClick={() => setFilterStatus('BEKLENIYOR')}
              style={{
                padding: '10px 20px',
                background: filterStatus === 'BEKLENIYOR' ? styles.warning : 'white',
                color: filterStatus === 'BEKLENIYOR' ? 'white' : styles.secondary,
                border: filterStatus === 'BEKLENIYOR' ? 'none' : `1px solid ${colors.border}`,
                borderRadius: radius.sm,
                cursor: 'pointer'
              }}
            >
              Bekleyen ({talepler.filter(t => t.durum === 'BEKLENIYOR').length})
            </button>
            <button
              onClick={() => setFilterStatus('REDDEDILDI')}
              style={{
                padding: '10px 20px',
                background: filterStatus === 'REDDEDILDI' ? styles.danger : 'white',
                color: filterStatus === 'REDDEDILDI' ? 'white' : styles.secondary,
                border: filterStatus === 'REDDEDILDI' ? 'none' : `1px solid ${colors.border}`,
                borderRadius: radius.sm,
                cursor: 'pointer'
              }}
            >
              Red Edilen ({talepler.filter(t => t.durum === 'REDDEDILDI').length})
            </button>
            <button
              onClick={() => setFilterStatus('ONAYLANDI')}
              style={{
                padding: '10px 20px',
                background: filterStatus === 'ONAYLANDI' ? styles.success : 'white',
                color: filterStatus === 'ONAYLANDI' ? 'white' : styles.secondary,
                border: filterStatus === 'ONAYLANDI' ? 'none' : `1px solid ${colors.border}`,
                borderRadius: radius.sm,
                cursor: 'pointer'
              }}
            >
              Onaylanan
            </button>
          </div>

          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Talep no / sube / malzeme ara"
              style={{ width: '280px', maxWidth: '100%', border: `1px solid ${colors.border}`, borderRadius: radius.sm, padding: '8px 10px', fontSize: '13px' }}
            />
            {isValidating && !loading && (
              <span style={{ fontSize: '12px', color: colors.textSecondary, background: '#f1f3f5', borderRadius: radius.lg, padding: '4px 10px' }}>
                Güncelleniyor...
              </span>
            )}
            <button
              onClick={() => navigate('/talep/olustur')}
              style={{ padding: '10px 20px', background: styles.success, color: 'white', border: 'none', borderRadius: radius.sm, cursor: 'pointer', fontWeight: 'bold' }}
            >
              Yeni Talep
            </button>
          </div>
        </div>

        <div style={{ background: colors.surface, borderRadius: radius.md, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: styles.secondary, color: 'white' }}>
                <th style={{ padding: '15px', textAlign: 'left' }}>Talep No</th>
                <th style={{ padding: '15px', textAlign: 'left' }}>Tarih</th>
                <th style={{ padding: '15px', textAlign: 'left' }}>Sube</th>
                <th style={{ padding: '15px', textAlign: 'left' }}>Malzeme</th>
                <th style={{ padding: '15px', textAlign: 'right' }}>Miktar</th>
                <th style={{ padding: '15px', textAlign: 'left' }}>Talep Eden</th>
                <th style={{ padding: '15px', textAlign: 'left' }}>Durum</th>
                <th style={{ padding: '15px', textAlign: 'center' }}>İşlem</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 8 }).map((_, row) => (
                  <tr key={`skeleton-${row}`} style={{ borderBottom: `1px solid ${colors.border}` }}>
                    {Array.from({ length: 8 }).map((__, col) => (
                      <td key={`skeleton-${row}-${col}`} style={{ padding: '15px' }}>
                        <div style={{ height: '12px', borderRadius: radius.sm, background: '#e9ecef', width: col === 3 ? '90%' : '70%' }} />
                      </td>
                    ))}
                  </tr>
                ))
              ) : filteredTalepler.length === 0 ? (
                <tr>
                  <td colSpan={8} style={{ padding: '24px', textAlign: 'center', color: colors.textTertiary }}>
                    Talep bulunamadı.
                  </td>
                </tr>
              ) : filteredTalepler.map(talep => (
                <tr key={talep.id} style={{ borderBottom: `1px solid ${colors.border}` }}>
                  <td style={{ padding: '15px', fontWeight: 'bold' }}>{talep.talep_no}</td>
                  <td style={{ padding: '15px' }}>{talep.tarih || new Date(talep.created_at).toLocaleDateString()}</td>
                  <td style={{ padding: '15px' }}>
                    {talep.lokasyon_ad || lokasyonlar.find(l => String(l.id) === String(talep.sube))?.ad || talep.sube}
                  </td>
                  <td style={{ padding: '15px' }}>
                    {talep.malzeme_ad || malzemeler.find(m => String(m.id) === String(talep.malzeme))?.ad || talep.malzeme}
                    {talep.serbest_urun_ad && (
                      <span style={{ fontSize: '10px', color: colors.textTertiary, display: 'block' }}>(serbest yazim)</span>
                    )}
                  </td>
                  <td style={{ padding: '15px', textAlign: 'right' }}>{talep.miktar} {talep.birim}</td>
                  <td style={{ padding: '15px' }}>{talep.talep_eden_ad || talep.talep_eden || 'Sistem'}</td>
                  <td style={{ padding: '15px' }}>
                    <StatusBadge status={talep.durum} />
                    {talep.durum === 'REDDEDILDI' && talep.red_nedeni && (
                      <div style={{ fontSize: '11px', color: styles.danger, marginTop: '4px', maxWidth: '150px' }}>
                        {talep.red_nedeni}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '15px', textAlign: 'center' }}>
                    {canApproveRequest && talep.durum === 'BEKLENIYOR' && (
                      <button
                        onClick={() => handleApproveTalep(talep.id)}
                        style={{ padding: '6px 12px', background: styles.success, color: 'white', border: 'none', borderRadius: radius.sm, cursor: 'pointer', fontSize: '12px' }}
                      >
                        Onayla
                      </button>
                    )}
                    {canRejectRequest && talep.durum === 'BEKLENIYOR' && (
                      <button
                        onClick={() => handleRejectClick(talep)}
                        style={{ padding: '6px 12px', background: styles.danger, color: 'white', border: 'none', borderRadius: radius.sm, cursor: 'pointer', fontSize: '12px', marginLeft: '5px' }}
                      >
                        Reddet
                      </button>
                    )}
                    {canConvertToOrder && talep.durum === 'ONAYLANDI' && (
                      <button
                        onClick={() => {
                          navigate('/siparis/olustur', { state: { transferredTalep: talep } });
                        }}
                        style={{
                          padding: '6px 12px',
                          background: styles.info,
                          color: 'white',
                          border: 'none',
                          borderRadius: radius.sm,
                          cursor: 'pointer',
                          fontSize: '12px',
                          marginTop: '5px'
                        }}
                      >
                        Sipariş Ver
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {showRejectModal && (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
            <div style={{ background: colors.surface, padding: '20px', borderRadius: radius.md, width: '400px', maxWidth: '92vw', boxSizing: 'border-box' }}>
              <h3>Talebi Reddet</h3>
              <p>Talep No: {targetTalep?.talep_no}</p>
              <textarea
                value={rejectReason}
                onChange={e => setRejectReason(e.target.value)}
                placeholder="Lütfen red sebebini yaziniz..."
                style={{ width: '100%', padding: '10px', height: '100px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, marginBottom: '10px' }}
              />
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <button onClick={() => setShowRejectModal(false)} style={{ padding: '10px', background: '#ccc', border: 'none', borderRadius: radius.sm, cursor: 'pointer' }}>İptal</button>
                <button onClick={confirmReject} style={{ padding: '10px', background: styles.danger, color: 'white', border: 'none', borderRadius: radius.sm, cursor: 'pointer' }}>Reddet</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TalepListesiPage;
